// ThreeDayView.jsx

import React, { useLayoutEffect, useMemo, useRef } from 'react';

// Context
import { useCalendarTimeline } from 'context/CalendarTimelineContext';

// Utilities
import { normalizeDateValue } from 'utilities/DateTime';
import { groupAllDayEventsByDateKey, groupTimedEventsByDateHour } from 'views/calendar/shared/calendarUtils';

// Hooks
import useCalendarDateFields from 'views/calendar/hooks/useCalendarDateFields';
import useCalendarEvents from 'views/calendar/hooks/useCalendarEvents';

// CSS
import './ThreeDayView.css';

// Components
import { addDays, startOfDay } from 'date-fns';
import CalendarNavControls from 'views/calendar/shared/CalendarNavControls';
import CalendarRangePanel from 'views/calendar/shared/CalendarRangePanel';

/**
 * ThreeDayView
 * 
 * Renders a three-day view for a calendar, showing events for the selected date and the following two days.
 * 
 * @param {Object} view - View configuration.
 * @param {Object} typeDef - Type definition for events.
 * @param {Array} nodes - Event nodes to display.
 * @param {string} language - Language code for localization (default: 'en').
 *  
 *
 * @returns {ReactNode} - ThreeDayView.
 */
const ThreeDayView = React.memo(({
    view,
    typeDef,
    nodes = [],
    eventItems,
    language = 'en',
    onNodeClick,
    onNodeDoubleClick,
    onAddDate,
    onAddDateTime,
    canDragEvents = false,
    draggingItem = null,
    dragOverTargetKey = null,
    onEventDragStart,
    onEventDragEnd,
    onDropTargetDragOver,
    onMoveToTarget,
}) => {

    // ----- CONTEXT ----- //

    // Global Context
    const {
        selectedDate,
        setSelectedDate,
    } = useCalendarTimeline();

    // ----- REFS ----- //

    const containerRef = useRef(null);

    // ----- HOOKS ----- //

    // Date fields from type definition

    const { typeKey, dateFields } = useCalendarDateFields(typeDef, { preferredFieldKey: view?.calendarField });

    // ----- MEMOS ----- //

    // Base date for the month view (normalized selected date or current date)

    const baseDate = useMemo(
        () => normalizeDateValue(selectedDate) || new Date(),
        [selectedDate]
    );

    // Today's date (used for highlighting current day)
    const rangeStart = useMemo(
        () => startOfDay(baseDate),
        [baseDate]
    );

    // Generate the three-day range starting from the base date
    const rangeDays = useMemo(() => (
        Array.from({ length: 3 }, (_, index) => addDays(rangeStart, index))
    ), [rangeStart]);

    // Fetch and process events for the three-day range
    const today = useMemo(() => new Date(), []);

    // Generate a label for the current three-day range (e.g., "March 1, 2024 - March 3, 2024")
    const rangeLabel = useMemo(() => {
        const rangeEnd = addDays(rangeStart, 2);
        const format = (date) => date.toLocaleDateString(language || 'en', {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
        });
        return `${format(rangeStart)} - ${format(rangeEnd)}`;
    }, [rangeStart, language]);

    // ----- HOOKS ----- //

    // Fetch and process events for the three-day range

    const resolvedEventItems = useCalendarEvents({
        nodes,
        dateFields,
        typeKey,
        eventItems,
        rangeStart,
        rangeEnd: addDays(rangeStart, 2),
    });

    // ----- MEMOS ----- //

    // Group events by date key for the three-day range

    const allDayEventsByDate = useMemo(() => {
        const rangeEnd = addDays(rangeStart, 2);

        return groupAllDayEventsByDateKey(
            resolvedEventItems,
            (item) => item.day >= rangeStart && item.day <= rangeEnd
        );
    }, [resolvedEventItems, rangeStart]);

    const timedEventsByDateHour = useMemo(() => {
        const rangeEnd = addDays(rangeStart, 2);

        return groupTimedEventsByDateHour(
            resolvedEventItems,
            (item) => item.day >= rangeStart && item.day <= rangeEnd
        );
    }, [resolvedEventItems, rangeStart]);

    // ----- HANDLERS ----- //

    /**
     * Handles changing the date range when navigating to previous or next days.
     * 
     * @param {number} direction - Direction to navigate (-1 for previous, 1 for next).
     */

    const handleRangeChange = (direction) => {
        const nextDate = addDays(baseDate, direction * 3);
        setSelectedDate(nextDate);
    };

    /**
     * Handles navigating to today's date when the "Today" button is clicked.
     */
    const handleGoToToday = () => {
        setSelectedDate(new Date());
    };

    // ----- EFFECTS ----- //

    useLayoutEffect(() => {
        const container = containerRef.current;
        const grid = container?.querySelector('.calendar-range-panel-grid');
        const scrollParent = container?.parentElement;
        if (!container || !grid || !scrollParent) return undefined;

        const syncHourRowMinHeight = () => {
            const availableHeight = scrollParent.clientHeight - grid.offsetTop;
            const hourRowMinHeight = Math.max(40, Math.floor(availableHeight / 24));
            container.style.setProperty('--calendar-range-hour-row-min', `${hourRowMinHeight}px`);
        };

        syncHourRowMinHeight();

        const resizeObserver = new ResizeObserver(syncHourRowMinHeight);
        resizeObserver.observe(scrollParent);
        resizeObserver.observe(container);

        return () => resizeObserver.disconnect();
    }, []);

    // ----- RENDER ----- //

    return (

        <div
          className="three-day-view-container"
          style={{
            '--calendar-range-columns': 3,
          }}
          ref={containerRef}
        >
            <CalendarNavControls
              title={rangeLabel}
              prevAriaLabel="Previous three days"
              nextAriaLabel="Next three days"
              onNext={() => handleRangeChange(1)}
              onPrev={() => handleRangeChange(-1)}
              onToday={handleGoToToday}
            />
            <CalendarRangePanel
              headerLabel="3 Day"
              days={rangeDays}
              selectedDate={baseDate}
              today={today}
              language={language}
              eventsByDate={allDayEventsByDate}
              allDayEventsByDate={allDayEventsByDate}
              timedEventsByDateHour={timedEventsByDateHour}
              canDragEvents={canDragEvents}
              draggingItem={draggingItem}
              dragOverTargetKey={dragOverTargetKey}
              onAddDate={onAddDate}
              onAddDateTime={onAddDateTime}
              onDropTargetDragOver={onDropTargetDragOver}
              onEventDragEnd={onEventDragEnd}
              onEventDragStart={onEventDragStart}
              onMoveToTarget={onMoveToTarget}
              onNodeClick={onNodeClick}
              onNodeDoubleClick={onNodeDoubleClick}
              onSelectDate={setSelectedDate}
            />
        </div>
    );

});

// Dev Tools
ThreeDayView.displayName = 'ThreeDayView';

export default ThreeDayView;
